import { analyzeText, detectPdfLike, detectTableLikeBlock, isListLine } from './detection';

const INVISIBLE_RE = /[\u200B-\u200D\uFEFF\u2060\u00AD]/g;

/**
 * Only merge when text looks PDF-wrapped; avoids joining unrelated short lines (e.g. after dedupe).
 * @param {import('./cleanupOptions').CleanupOptions} options
 * @param {{ pdfLike: boolean }} signals
 * @param {string[]} lines current lines (e.g. after dedupe)
 */
function effectiveMerge(options, signals, lines) {
  if (options.keepOriginalLineBreaks || options.flattenToOneLine) return false;
  const pdfLike = signals.pdfLike || detectPdfLike(lines);
  if (!pdfLike) return false;
  return !!(options.mergeBrokenLines || options.smartCleanup);
}

function normalizeLineEndings(text) {
  return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

function removeInvisible(text) {
  return text.replace(INVISIBLE_RE, '');
}

function replaceNbsp(text) {
  return text.replace(/\u00A0/g, ' ');
}

function expandTabs(line, tabWidth) {
  if (!line.includes('\t')) return line;
  let out = '';
  let col = 0;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '\t') {
      const spaces = tabWidth - (col % tabWidth);
      out += ' '.repeat(spaces);
      col += spaces;
    } else {
      out += ch;
      col = ch === '\n' ? 0 : col + 1;
    }
  }
  return out;
}

/**
 * Split into paragraph groups (arrays of lines); null marks preserved blank runs.
 * @param {string[]} lines
 */
function groupByParagraphs(lines, preserveParagraphs) {
  /** @type {(string[] | null)[]} */
  const out = [];
  let current = [];
  let blankRun = 0;

  const flushPara = () => {
    if (current.length) {
      out.push(current);
      current = [];
    }
  };

  for (const line of lines) {
    const isBlank = line.trim() === '';
    if (isBlank) {
      flushPara();
      blankRun += 1;
    } else {
      if (blankRun > 0) {
        if (preserveParagraphs) {
          for (let b = 0; b < blankRun; b += 1) out.push(null);
        } else {
          out.push(null);
        }
        blankRun = 0;
      }
      current.push(line);
    }
  }
  flushPara();
  if (blankRun > 0 && preserveParagraphs) {
    for (let b = 0; b < blankRun; b += 1) out.push(null);
  } else if (blankRun > 0) {
    out.push(null);
  }
  return out;
}

/**
 * @param {string[]} groupLines
 * @param {import('./cleanupOptions').CleanupOptions} options
 * @param {boolean} mergeOn
 */
function mergeWithinParagraph(groupLines, options, mergeOn) {
  if (!mergeOn || groupLines.length === 0) return groupLines;

  if (options.preserveLists || options.smartCleanup) {
    const anyList = groupLines.some((l) => isListLine(l));
    if (anyList) return groupLines;
  }

  if (options.smartCleanup && detectTableLikeBlock(groupLines)) {
    return groupLines;
  }

  const merged = groupLines
    .map((l) => l.trim())
    .filter((l, i, arr) => !(l === '' && i > 0))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  return merged ? [merged] : [];
}

/**
 * @param {string[]} lines
 * @param {import('./cleanupOptions').CleanupOptions} options
 * @param {{ pdfLike: boolean }} signals
 */
function applyLineMerging(lines, options, signals) {
  const mergeOn = effectiveMerge(options, signals, lines);
  if (!mergeOn) return lines;

  const preserveParagraphs = options.preserveParagraphs || options.smartCleanup;
  const groups = groupByParagraphs(lines, preserveParagraphs);

  /** @type {string[]} */
  const result = [];

  for (const g of groups) {
    if (g === null) {
      result.push('');
      continue;
    }
    const merged = mergeWithinParagraph(g, options, true);
    for (const line of merged) {
      result.push(line);
    }
  }
  return result;
}

function trimLine(line, options) {
  if (options.trimLines) return line.trim();
  if (options.trailingSpaces) return line.replace(/[ \t]+$/g, '');
  return line;
}

function removeExtraSpacesInLine(line) {
  return line.replace(/[ \t]{2,}/g, ' ');
}

function normalizeQuotesInText(text, on) {
  if (!on) return text;
  return text
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2018\u2019]/g, "'");
}

function normalizeDashesInText(text, on) {
  if (!on) return text;
  return text.replace(/\u2013/g, '-').replace(/\u2014/g, '--');
}

/**
 * @param {string} text
 * @param {number} maxBlankLines max blank lines between content (1 or 2 typical)
 */
function collapseBlankLinesInText(text, maxBlankLines) {
  const n = Math.min(4, Math.max(1, maxBlankLines));
  const maxNewlines = n + 1;
  const pattern = new RegExp(`\\n{${maxNewlines + 1},}`, 'g');
  const replacement = '\n'.repeat(maxNewlines);
  return text.replace(pattern, replacement);
}

/**
 * Remove duplicate lines using trim-normalized keys; first occurrence wins; order preserved.
 * Empty lines dedupe like any other line (repeated blanks collapse to one).
 * Must run before merge / flatten so lines are not merged into one string first.
 * @param {string[]} lines
 * @returns {string[]}
 */
function removeDuplicateLinesFromLines(lines) {
  const seen = new Set();
  const out = [];
  for (const line of lines) {
    const key = line.trim();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(line);
  }
  return out;
}

/**
 * @param {string} input
 * @param {import('./cleanupOptions').CleanupOptions} options
 */
export function cleanText(input, options) {
  if (input === '') {
    return {
      output: '',
      stats: {
        issuesFixed: 0,
        spacesNormalized: 0,
        blankLinesReduced: 0,
        invisibleRemoved: 0,
        linesMergedHint: 0,
        inputChars: 0,
        outputChars: 0,
        inputLines: 0,
        outputLines: 0,
      },
    };
  }

  let text = input;
  const beforeLines = normalizeLineEndings(text).split('\n');
  const lineCountBefore = beforeLines.length;

  let invisibleRemoved = 0;
  if (options.removeInvisibleChars) {
    const next = removeInvisible(text);
    invisibleRemoved = text.length - next.length;
    text = next;
  }

  text = normalizeLineEndings(text);

  if (options.normalizeWhitespace) {
    text = replaceNbsp(text);
  }

  if (options.tabsToSpaces) {
    text = text
      .split('\n')
      .map((l) => expandTabs(l, options.tabWidth))
      .join('\n');
  }

  /** 1–4: lines, trim per options (before dedupe + merge). */
  let lines = text.split('\n').map((line) => trimLine(line, options));

  /** 5: duplicate lines at line granularity before merge / flatten. */
  if (options.removeDuplicateLines) {
    lines = removeDuplicateLinesFromLines(lines);
  }

  /** 6–7: content detection + merge (paragraphs / lists preserved inside merger). */
  const textAfterDedupe = lines.join('\n');
  const signals = analyzeText(textAfterDedupe);

  if (!options.keepOriginalLineBreaks && !options.flattenToOneLine) {
    lines = applyLineMerging(lines, options, signals);
  }

  /** 9: extra spaces inside each line (after merges may have joined spacing). */
  lines = lines.map((line) => {
    let l = line;
    if (options.removeExtraSpaces) l = removeExtraSpacesInLine(l);
    l = trimLine(l, options);
    return l;
  });

  let joined = lines.join('\n');

  /** 10: collapse blank lines */
  let blankLinesReduced = 0;
  if (options.collapseBlankLines) {
    const longRunsBefore = (joined.match(/\n{3,}/g) || []).length;
    joined = collapseBlankLinesInText(joined, options.maxConsecutiveBlankLines);
    const longRunsAfter = (joined.match(/\n{3,}/g) || []).length;
    blankLinesReduced = Math.max(0, longRunsBefore - longRunsAfter);
  }

  /** 11: punctuation / dash normalization */
  joined = normalizeQuotesInText(joined, options.normalizeQuotes);
  joined = normalizeDashesInText(joined, options.normalizeDashes);

  if (options.flattenToOneLine) {
    joined = joined
      .split(/\n+/)
      .map((l) => l.trim())
      .filter(Boolean)
      .join(' ');
    joined = removeExtraSpacesInLine(joined).trim();
  }

  const spaceRunsBefore = (input.match(/[ \t]{2,}/g) || []).length;
  const spaceRunsAfter = (joined.match(/[ \t]{2,}/g) || []).length;
  const spacesNormalized = Math.max(0, spaceRunsBefore - spaceRunsAfter);

  const outLines = joined.split('\n').length;
  const linesMergedHint = Math.max(0, lineCountBefore - outLines);

  const stats = {
    spacesNormalized,
    blankLinesReduced,
    invisibleRemoved,
    linesMergedHint,
    inputChars: input.length,
    outputChars: joined.length,
    inputLines: lineCountBefore,
    outputLines: outLines,
    issuesFixed: 0,
  };

  let issues = 0;
  if (spacesNormalized > 0) issues += 1;
  if (blankLinesReduced > 0) issues += 1;
  if (invisibleRemoved > 0) issues += 1;
  if (linesMergedHint > 0) issues += 1;
  if (input !== joined && issues === 0) issues = 1;
  stats.issuesFixed = issues;

  return { output: joined, stats };
}
